import { validateNumber, validatePercentage } from "./validateFields";
import { numberError, percentageError } from "./validationMessage";

const isEmptyTableValue = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

export const validateLeadershipTable = (
  inputData,
  tables,
  setErrors,
  fieldName,
  parameter,
  parameter2,
) => {
  const newErrors = {};
  const urlPattern =
    /^(https?:\/\/)?(www\.)?(([\p{L}0-9-]+\.)+[\p{L}]{2,}|(\d{1,3}\.){3}\d{1,3})(:\d{2,5})?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/u;

  tables?.forEach((table, tableIndex) => {
    table?.rows?.forEach((row, rowIndex) => {
      const filteredRow = Object.fromEntries(
        Object.entries(row).filter(
          ([key, val]) =>
            key !== "_id" &&
            key !== parameter &&
            key !== parameter2 &&
            val !== null &&
            val !== undefined &&
            val !== "" &&
            (!Array.isArray(val) || val.length !== 0), // Keeps non-empty arrays, removes empty ones
        ),
      );

      if (Object.keys(filteredRow)?.length > 0) {
        inputData[tableIndex]?.fields?.forEach((field) => {
          if (
            (field.fieldName === "link" || field.fieldName === "webLink") &&
            row[field.fieldName] &&
            !urlPattern.test(row[field.fieldName])
          ) {
            if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
            if (!newErrors[tableIndex][rowIndex])
              newErrors[tableIndex][rowIndex] = {};
            newErrors[tableIndex][rowIndex][field.fieldName] =
              "Enter a valid URL";
          } else if (
            isEmptyTableValue(row[field.fieldName]) &&
            field.fieldName !== "link" &&
            field.fieldName !== "webLink"
          ) {
            if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
            if (!newErrors[tableIndex][rowIndex])
              newErrors[tableIndex][rowIndex] = {};
            newErrors[tableIndex][rowIndex][field.fieldName] = "Required";
          }
        });
      }
    });
  });

  setErrors((prev) =>
    fieldName
      ? {
          ...prev,
          [fieldName]: newErrors,
        }
      : newErrors,
  );
  return Object.keys(newErrors).length === 0;
};

export const validateTable = (inputData, tables, setErrors, fieldName) => {
  const newErrors = {};
  const urlPattern =
    /^(https?:\/\/)?(www\.)?(([\p{L}0-9-]+\.)+[\p{L}]{2,}|(\d{1,3}\.){3}\d{1,3})(:\d{2,5})?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/u;

  tables?.forEach((table, tableIndex) => {
    table?.rows?.forEach((row, rowIndex) => {
      inputData[tableIndex]?.fields.forEach((field) => {
        if (
          field.fieldName === "link" &&
          row[field.fieldName] &&
          !urlPattern.test(row[field.fieldName])
        ) {
          if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
          if (!newErrors[tableIndex][rowIndex])
            newErrors[tableIndex][rowIndex] = {};
          newErrors[tableIndex][rowIndex][field.fieldName] =
            "Enter a valid URL";
        } else if (
          isEmptyTableValue(row[field.fieldName]) &&
          field.fieldName !== "link"
        ) {
          if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
          if (!newErrors[tableIndex][rowIndex])
            newErrors[tableIndex][rowIndex] = {};
          newErrors[tableIndex][rowIndex][field.fieldName] = "Required";
        }
      });
    });
  });

  setErrors((prev) =>
    fieldName
      ? {
          ...prev,
          [fieldName]: newErrors,
        }
      : newErrors,
  );
  return Object.keys(newErrors).length === 0;
};

export const validateFields = (
  tables,
  inputData,
  defaultSegments,
  setErrors,
  errors,
  name,
) => {
  const newErrors = {};

  defaultSegments.forEach((segment) => {
    const fieldErrors = {};

    inputData.forEach((section) => {
      const segmentData = tables[segment.field] || {};

      // Loop through each field in the section
      section.fields.forEach((field) => {
        const value =
          segmentData[field.fieldName] === 0 ? 0 : segmentData[field.fieldName];

        if (value === "" || value?.length === 0) {
          fieldErrors[field.title] = "Required";
          return;
        }

        if (field.type === "number" && !validateNumber(value)) {
          fieldErrors[field.title] = numberError;
        }

        if (field.type === "percentage" && !validatePercentage(value)) {
          fieldErrors[field.title] = percentageError;
        }
      });
    });

    // Add errors for the section
    if (Object.keys(fieldErrors).length > 0) {
      newErrors[segment.title] = fieldErrors;
    }
  });

  setErrors((prev) => ({
    ...prev, // Spread the previous errors
    [name]: newErrors, // Update or add the new error for the specific field (or name)
  }));

  return Object.keys(newErrors).length === 0; // Returns true if no errors
};

export const validateLastRow = (
  inputData,
  tables,
  setErrors,
  fieldName,
  parameter,
) => {
  const newErrors = {};
  const urlPattern =
    /^(https?:\/\/)?(www\.)?(([\p{L}0-9-]+\.)+[\p{L}]{2,}|(\d{1,3}\.){3}\d{1,3})(:\d{2,5})?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/u;

  tables?.forEach((table, tableIndex) => {
    const rowIndex = table?.rows?.length - 1;
    const lastRow = table?.rows?.[rowIndex];

    inputData[tableIndex]?.fields.forEach((field) => {
      if (
        field.fieldName === "link" &&
        lastRow[field.fieldName] &&
        !urlPattern.test(lastRow[field.fieldName])
      ) {
        if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
        if (!newErrors[tableIndex][rowIndex])
          newErrors[tableIndex][rowIndex] = {};
        newErrors[tableIndex][rowIndex][field.fieldName] = "Enter a valid URL";
      } else if (
        isEmptyTableValue(lastRow[field.fieldName]) &&
        field.fieldName !== "link"
      ) {
        if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
        if (!newErrors[tableIndex][rowIndex])
          newErrors[tableIndex][rowIndex] = {};
        newErrors[tableIndex][rowIndex][field.fieldName] = "Required";
      }
    });
  });

  setErrors((prev) =>
    fieldName
      ? {
          ...prev,
          [fieldName]: newErrors,
        }
      : newErrors,
  );
  return Object.keys(newErrors).length === 0;
};

export const validateOptionalTable = (
  inputData,
  tables,
  setErrors,
  fieldName,
  parameter,
  defaultSegments,
) => {
  const newErrors = {};
  const urlPattern =
    /^(https?:\/\/)?(www\.)?(([\p{L}0-9-]+\.)+[\p{L}]{2,}|(\d{1,3}\.){3}\d{1,3})(:\d{2,5})?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/u;

  tables?.forEach((table, tableIndex) => {
    table?.rows?.forEach((row, rowIndex) => {
      const filteredRow = Object.fromEntries(
        Object.entries(row).filter(
          ([key, val]) =>
            key !== "_id" &&
            key !== parameter &&
            val !== null &&
            val !== undefined &&
            val !== "" &&
            (!Array.isArray(val) || val.length !== 0), // Keeps non-empty arrays, removes empty ones
        ),
      );

      if (
        Object.keys(filteredRow)?.length > 0 ||
        (defaultSegments
          ? table?.rows?.length > defaultSegments?.length
          : table?.rows?.length > 1)
      ) {
        inputData[tableIndex]?.fields.forEach((field) => {
          if (
            field.fieldName === "link" &&
            row[field.fieldName] &&
            !urlPattern.test(row[field.fieldName])
          ) {
            if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
            if (!newErrors[tableIndex][rowIndex])
              newErrors[tableIndex][rowIndex] = {};
            newErrors[tableIndex][rowIndex][field.fieldName] =
              "Enter a valid URL";
          } else if (
            isEmptyTableValue(row[field.fieldName]) &&
            field.fieldName !== "link"
          ) {
            if (!newErrors[tableIndex]) newErrors[tableIndex] = [];
            if (!newErrors[tableIndex][rowIndex])
              newErrors[tableIndex][rowIndex] = {};
            newErrors[tableIndex][rowIndex][field.fieldName] = "Required";
          }
        });
      }
    });
  });

  setErrors((prev) =>
    fieldName
      ? {
          ...prev,
          [fieldName]: newErrors,
        }
      : newErrors,
  );
  return Object.keys(newErrors).length === 0;
};
